/**
 * Inbound confirmations inbox — mirrors iOS InboundConfirmationsControl (list/detail slice).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Spin, message } from 'antd';
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { inboundConfirmationsEn } from '../../content/trips.en';
import {
  type InboundConfirmation,
  type InboundEmailInfo,
  InboundEmailNotProvisionedError,
  confirmationStatus,
  getInboundConfirmation,
  getInboundEmail,
  listInboundConfirmations,
  patchInboundConfirmation,
  pendingConfirmationsCount,
} from '../../services/inboundEmailService';
import InboundPlacementModal from '../InboundPlacementModal/InboundPlacementModal';
import styles from './InboundConfirmationsInbox.module.css';

export interface InboundConfirmationsInboxProps {
  open: boolean;
  onClose: () => void;
  /** Prefill trip when opening placement. */
  defaultTripId?: string;
  onPendingCountChange?: (count: number) => void;
}

function statusLabel(status: string): string {
  const s = confirmationStatus({ status } as InboundConfirmation);
  if (s === 'completed') return inboundConfirmationsEn.statusCompleted;
  if (s === 'imported') return inboundConfirmationsEn.statusImported;
  return inboundConfirmationsEn.statusReceived;
}

function formatReceived(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const InboundConfirmationsInbox: React.FC<InboundConfirmationsInboxProps> = ({
  open,
  onClose,
  defaultTripId,
  onPendingCountChange,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<InboundEmailInfo | null>(null);
  const [items, setItems] = useState<InboundConfirmation[]>([]);
  const [detail, setDetail] = useState<InboundConfirmation | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [placementOpen, setPlacementOpen] = useState(false);

  const notifyPending = useCallback(
    (list: InboundConfirmation[]): void => {
      onPendingCountChange?.(pendingConfirmationsCount(list));
    },
    [onPendingCountChange]
  );

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [emailInfo, page] = await Promise.all([
        getInboundEmail().catch((err: unknown) => {
          if (err instanceof InboundEmailNotProvisionedError) return null;
          throw err;
        }),
        listInboundConfirmations({ limit: 50 }),
      ]);
      setInfo(emailInfo);
      setItems(page.items);
      notifyPending(page.items);
    } catch (err) {
      if (err instanceof InboundEmailNotProvisionedError) {
        setError(inboundConfirmationsEn.notProvisioned);
        setItems([]);
        notifyPending([]);
      } else {
        setError(inboundConfirmationsEn.loadFailed);
      }
    } finally {
      setLoading(false);
    }
  }, [notifyPending]);

  useEffect(() => {
    if (!open) {
      setDetail(null);
      return;
    }
    void load();
  }, [open, load]);

  const copyAddress = async (): Promise<void> => {
    if (!info?.inboundEmailAddress) return;
    try {
      await navigator.clipboard.writeText(info.inboundEmailAddress);
      message.success(inboundConfirmationsEn.addressCopied);
    } catch {
      message.error(inboundConfirmationsEn.loadFailed);
    }
  };

  const openDetail = async (id: string): Promise<void> => {
    setDetailLoading(true);
    try {
      const full = await getInboundConfirmation(id);
      setDetail(full);
    } catch {
      const fallback = items.find((i) => i.id === id) ?? null;
      setDetail(fallback);
      if (!fallback) message.error(inboundConfirmationsEn.loadFailed);
    } finally {
      setDetailLoading(false);
    }
  };

  const markComplete = async (id: string): Promise<void> => {
    setCompletingId(id);
    try {
      const updated = await patchInboundConfirmation(id, 'completed');
      setItems((prev) => {
        const next = prev.map((i) => (i.id === id ? updated : i));
        notifyPending(next);
        return next;
      });
      setDetail((prev) => (prev?.id === id ? updated : prev));
    } catch {
      message.error(inboundConfirmationsEn.markCompleteFailed);
    } finally {
      setCompletingId(null);
    }
  };

  const handleImported = async (confirmationId: string, tripId: string): Promise<void> => {
    try {
      const updated = await patchInboundConfirmation(confirmationId, 'imported', tripId);
      setItems((prev) => {
        const next = prev.map((i) => (i.id === confirmationId ? updated : i));
        notifyPending(next);
        return next;
      });
      setDetail((prev) => (prev?.id === confirmationId ? updated : prev));
    } catch {
      message.error(inboundConfirmationsEn.markCompleteFailed);
    }
  };

  const pending = pendingConfirmationsCount(items);
  const subjectOf = (c: InboundConfirmation): string => {
    const t = c.subject?.trim();
    return t || inboundConfirmationsEn.subjectFallback;
  };

  return (
    <Modal
      title={
        <div className={styles.titleRow}>
          <span>{inboundConfirmationsEn.title}</span>
          {pending > 0 ? (
            <span className={styles.pendingPill}>{inboundConfirmationsEn.pending(pending)}</span>
          ) : null}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      destroyOnHidden
      data-testid="inbound-confirmations-inbox"
    >
      <div className={styles.toolbar}>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={() => void load()}
          loading={loading}
        >
          {inboundConfirmationsEn.refresh}
        </Button>
      </div>

      <div className={styles.addressBlock}>
        <div className={styles.addressLabel}>{inboundConfirmationsEn.inboundAddress}</div>
        {info?.inboundEmailAddress ? (
          <div className={styles.addressRow}>
            <code className={styles.address}>{info.inboundEmailAddress}</code>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => void copyAddress()}
              aria-label={inboundConfirmationsEn.copyAddress}
            >
              {inboundConfirmationsEn.copyAddress}
            </Button>
          </div>
        ) : (
          <p className={styles.muted}>{inboundConfirmationsEn.addressUnavailable}</p>
        )}
      </div>

      {error ? <p className={styles.error}>{error}</p> : null}

      {loading && items.length === 0 ? (
        <div className={styles.centered}>
          <Spin />
        </div>
      ) : detail || detailLoading ? (
        <div className={styles.detail}>
          <Button type="link" className={styles.back} onClick={() => setDetail(null)}>
            {inboundConfirmationsEn.backToList}
          </Button>
          {detailLoading || !detail ? (
            <div className={styles.centered}>
              <Spin />
            </div>
          ) : (
            <>
              <h3 className={styles.detailSubject}>{subjectOf(detail)}</h3>
              <p className={styles.meta}>
                {inboundConfirmationsEn.from}: {detail.fromAddress}
              </p>
              <p className={styles.meta}>
                {inboundConfirmationsEn.received}: {formatReceived(detail.receivedAt)}
              </p>
              <p className={styles.meta}>{statusLabel(detail.status)}</p>
              <div className={styles.bodyPreview}>
                {detail.bodyPreview?.trim() ||
                  (detail.bodyHtml
                    ? detail.bodyHtml.replace(/<[^>]+>/g, ' ').slice(0, 800)
                    : inboundConfirmationsEn.noBody)}
              </div>
              {confirmationStatus(detail) === 'received' ? (
                <div className={styles.detailActions}>
                  <Button type="primary" onClick={() => setPlacementOpen(true)}>
                    {inboundConfirmationsEn.importToTrip}
                  </Button>
                  <Button
                    loading={completingId === detail.id}
                    onClick={() => void markComplete(detail.id)}
                  >
                    {inboundConfirmationsEn.markComplete}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : items.length === 0 ? (
        <p className={styles.empty} data-testid="inbound-empty">
          {info?.inboundEmailAddress
            ? inboundConfirmationsEn.emptyWithAddress(info.inboundEmailAddress)
            : inboundConfirmationsEn.empty}
        </p>
      ) : (
        <ul className={styles.list} data-testid="inbound-list">
          {items.map((c) => {
            const count = c.attachmentCount ?? c.attachments?.length ?? 0;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  className={styles.card}
                  onClick={() => void openDetail(c.id)}
                >
                  <div className={styles.cardTitle}>{subjectOf(c)}</div>
                  <div className={styles.cardMeta}>{c.fromAddress}</div>
                  <div className={styles.cardMeta}>
                    {formatReceived(c.receivedAt)} · {statusLabel(c.status)}
                    {count > 0 ? ` · ${inboundConfirmationsEn.attachments(count)}` : ''}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <InboundPlacementModal
        open={placementOpen}
        confirmation={detail}
        userId={user?.sub ?? ''}
        defaultTripId={defaultTripId}
        onClose={() => setPlacementOpen(false)}
        onImported={(id, tripId) => void handleImported(id, tripId)}
      />
    </Modal>
  );
};

export default InboundConfirmationsInbox;
